import { ArrowRightOutlined, CheckCircleOutlined, EditOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import axios from 'axios';
import { BASE_URL } from 'constants/api';
import { contentService } from 'content/service/contentService';
import React, { useEffect, useState } from 'react';
import { I18nFieldData } from '../../../shared/types/i18n/i18nTypes';
import { seasonContentService } from '../season/services/seasonContentService';

const { Text } = Typography;

interface ContentDetailResponse {
  id: number;
  i18n_title?: I18nFieldData;
  episode_order?: number;
  content_type?: string;
}

interface EpisodeTitlePreview {
  id: number;
  episodeOrder: number;
  contentType: string;
  currentTitle: I18nFieldData;
  newTitle: I18nFieldData; // Formatted title based on pattern (always generated)
  needsUpdate: boolean; // True if current title is empty OR matches old pattern
  key: number;
}

interface EpisodeTitleBatchUpdateModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  seasonId: number;
  seasonTitle: I18nFieldData;
}

interface FormatPatterns {
  en: string;
  ko: string;
  zh: string;
}

/**
 * Generate episode title format per language
 */
const generateEpisodeTitle = (
  lang: string,
  seasonTitle: string | undefined,
  episodeOrder: number,
  patterns: FormatPatterns
): string | undefined => {
  if (!seasonTitle) return undefined;

  let pattern = '';
  switch (lang) {
    case 'en':
      pattern = patterns.en;
      break;
    case 'ko':
      pattern = patterns.ko;
      break;
    case 'zh-hans':
    case 'zh-hant':
      pattern = patterns.zh;
      break;
    default:
      return undefined;
  }

  return pattern.replace('{season}', seasonTitle).replace('{number}', episodeOrder.toString());
};

/**
 * Fetch content details in bulk using ids parameter
 * Backend supports: GET /api/v1/content/?ids=1,2,3
 * Backend page_size max is 100, so we chunk the ids
 */
const CHUNK_SIZE = 100;

const fetchContentDetailsByIds = async (contentIds: number[]): Promise<ContentDetailResponse[]> => {
  if (contentIds.length === 0) return [];

  // Chunk ids by 100 for parallel requests
  const chunks: number[][] = [];
  for (let i = 0; i < contentIds.length; i += CHUNK_SIZE) {
    chunks.push(contentIds.slice(i, i + CHUNK_SIZE));
  }

  const results = await Promise.all(
    chunks.map(chunk =>
      contentService.fetchByIds(chunk.join(','), {
        page_size: String(CHUNK_SIZE),
      })
    )
  );

  // Convert ContentInterface to ContentDetailResponse
  // Ensure i18n_title is properly passed
  return results.flatMap(r =>
    r.results.map(item => ({
      id: item.id,
      i18n_title: item.i18n_title,
      episode_order: item.episode_order,
      content_type: item.content_type,
    }))
  ) as ContentDetailResponse[];
};

/**
 * Update content i18n_title
 */
const updateContentTitle = async (contentId: number, i18nTitle: I18nFieldData): Promise<void> => {
  await axios.patch(`${BASE_URL}/api/v1/content/${contentId}/`, {
    i18n_title: i18nTitle,
  });
};

/**
 * Auto-selection logic
 */
const shouldAutoSelect = (current: string | undefined, _lang: string, contentType: string) => {
  // 1. Content Type Check: Must be 'episode' or 'tv-series' (User request)
  // Normalizing to lowercase for safety
  const safeType = (contentType || '').toLowerCase();
  if (!['episode', 'tv-series'].includes(safeType)) {
    return false;
  }

  // 2. Empty Title Check
  if (!current) return true;

  return false;
};

const EpisodeTitleBatchUpdateModal: React.FC<EpisodeTitleBatchUpdateModalProps> = ({
  visible,
  onClose,
  onSuccess,
  seasonId,
  seasonTitle,
}) => {
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [previews, setPreviews] = useState<EpisodeTitlePreview[]>([]);
  const [contents, setContents] = useState<ContentDetailResponse[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Format Patterns State
  const [formatPatterns, setFormatPatterns] = useState<FormatPatterns>({
    en: '{season} : Episode {number}',
    ko: '{season} : {number}회',
    zh: '{season} : 第{number}集',
  });

  // Supported languages (excluding vi)
  const supportedLanguages = ['en', 'ko', 'zh-hans'];
  // Languages to display
  const displayLanguages = ['en', 'ko', 'zh-hans'];

  useEffect(() => {
    if (visible && seasonId) {
      fetchContents();
    }
  }, [visible, seasonId]);

  // Regenerate previews when pattern changes or contents are loaded
  useEffect(() => {
    if (contents.length > 0 && seasonTitle) {
      generatePreviews();
    }
  }, [contents, formatPatterns, seasonTitle]);

  const fetchContents = async () => {
    setLoading(true);
    try {
      const seasonContents = await seasonContentService.getSeasonContents(seasonId);
      const initialContents = seasonContents.contents || [];

      if (initialContents.length === 0) {
        setContents([]);
        setLoading(false);
        return;
      }

      // Fetch all content details at once using ids parameter (N+1 -> 1 request)
      const contentIds = initialContents.map(c => c.id);
      const detailedResults = await fetchContentDetailsByIds(contentIds);

      // Convert detail results to id-based Map for fast lookup
      const detailMap = new Map(detailedResults.map(d => [d.id, d]));

      // Merge basic season contents info with detail info
      // Ensure detail's i18n_title is properly passed
      const mergedContents: ContentDetailResponse[] = initialContents.map(content => {
        const detail = detailMap.get(content.id);
        if (detail) {
          // Use all fields from detail if available
          // detail includes i18n_title
          return {
            ...detail, // All fields from detail (including id, i18n_title, episode_order, content_type)
            // Supplement with content's basic info (fields that may not be in detail)
            episode_order: detail.episode_order ?? content.episode_order,
            content_type: detail.content_type ?? content.content_type,
          };
        }
        // Use only basic info if detail not available (no i18n_title)
        return {
          id: content.id,
          episode_order: content.episode_order,
          content_type: content.content_type,
          i18n_title: undefined,
        };
      });

      setContents(mergedContents);
    } catch (error) {
      console.error('Failed to fetch contents:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePreviews = () => {
    const previewList: EpisodeTitlePreview[] = [];
    const newSelectedIds = new Set<number>();

    for (const content of contents) {
      // Ensure i18n_title is properly passed
      // Check if content.i18n_title is an object with actual values
      const currentTitle: I18nFieldData =
        content.i18n_title && typeof content.i18n_title === 'object' ? content.i18n_title : {};
      const newTitle: I18nFieldData = { ...currentTitle }; // Start with current, fallback to generated

      const episodeOrder = content.episode_order || 1;
      let needsUpdate = false;

      // Determine content type safely
      const contentType = content.content_type || 'unknown';

      for (const lang of supportedLanguages) {
        const seasonTitleForLang = (seasonTitle as any)[lang];
        const currentValue = (currentTitle as any)[lang];

        if (seasonTitleForLang) {
          // Always generate new title based on pattern
          const generatedTitle = generateEpisodeTitle(
            lang,
            seasonTitleForLang,
            episodeOrder,
            formatPatterns
          );

          if (generatedTitle) {
            (newTitle as any)[lang] = generatedTitle;

            // Check if we should auto-select this episode
            if (shouldAutoSelect(currentValue, lang, contentType)) {
              needsUpdate = true;
            }
          }
        }
      }

      // If needs update, auto-select
      if (needsUpdate) {
        newSelectedIds.add(content.id);
      }

      // We don't merge with existing selections here to keep
      // the "Auto Select" logic reactive to Pattern/Data changes.
      // Resetting based on our smart logic is acceptable.

      previewList.push({
        id: content.id,
        key: content.id,
        episodeOrder,
        contentType,
        currentTitle,
        newTitle,
        needsUpdate,
      });
    }

    previewList.sort((a, b) => a.episodeOrder - b.episodeOrder);
    setPreviews(previewList);

    // Setting it every time generatePreviews runs is correct
    // because 'needsUpdate' definition depends on pattern.
    setSelectedIds(newSelectedIds);
  };

  const handleSelect = (id: number, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(previews.map(p => p.id));
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleConfirm = async () => {
    if (selectedIds.size === 0) {
      message.info('Please select items to update.');
      return;
    }

    setUpdating(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      const itemsToUpdate = previews.filter(p => selectedIds.has(p.id));

      for (const item of itemsToUpdate) {
        try {
          const titleUpdate: I18nFieldData = { ...item.currentTitle };

          supportedLanguages.forEach(lang => {
            const generated = (item.newTitle as any)[lang];
            // If we have a generated title for this language, use it.
            if (generated) {
              (titleUpdate as any)[lang] = generated;
            }
          });

          await updateContentTitle(item.id, titleUpdate);
          successCount++;
        } catch (error) {
          console.error(`Failed to update content ${item.id}:`, error);
          errorCount++;
        }
      }

      if (errorCount > 0) {
        message.warning(`${successCount} succeeded, ${errorCount} failed`);
      } else {
        message.success(`${successCount} title(s) updated successfully.`);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Batch update failed:', error);
    } finally {
      setUpdating(false);
    }
  };

  // Episodes that need changes (for status display)
  const episodesWithChanges = previews.filter(p => p.needsUpdate);

  const columns = [
    {
      title: 'Episode',
      dataIndex: 'episodeOrder',
      key: 'episodeOrder',
      width: 100,
      render: (order: number, record: EpisodeTitlePreview) => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <Tag>{`EP ${order}`}</Tag>
          <Tag color="cyan" style={{ fontSize: 10, margin: 0 }}>
            {record.contentType}
          </Tag>
          <div style={{ marginTop: 4 }}>
            {record.needsUpdate ? (
              <EditOutlined style={{ color: '#faad14', fontSize: 16 }} title="Needs Update" />
            ) : (
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} title="Done" />
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Title Status',
      key: 'titleStatus',
      render: (_: any, record: EpisodeTitlePreview) => {
        // Is this item selected?
        const isSelected = selectedIds.has(record.id);

        return (
          <div style={{ display: 'flex', gap: 16 }}>
            {displayLanguages.map(lang => {
              const currentValue = (record.currentTitle as any)[lang];
              const newValue = (record.newTitle as any)[lang];
              const seasonTitleForLang = (seasonTitle as any)[lang];

              const isDifferent = currentValue !== newValue;
              const showPreview = isSelected && isDifferent && newValue;

              return (
                <div key={lang} style={{ flex: 1, minWidth: 200, maxWidth: 300 }}>
                  <div style={{ marginBottom: 4 }}>
                    <Tag
                      color={
                        showPreview
                          ? 'orange'
                          : currentValue !== null && currentValue !== ''
                            ? 'green'
                            : 'default'
                      }
                    >
                      {lang === 'zh-hans' ? 'zh' : lang}
                    </Tag>
                  </div>

                  {/* Current Value */}
                  <div
                    style={{
                      color: currentValue !== null && currentValue !== '' ? '#52c41a' : '#999',
                      fontStyle: currentValue !== null && currentValue !== '' ? 'normal' : 'italic',
                      fontSize: 13,
                      minHeight: 20,
                      marginBottom: 4,
                      wordBreak: 'break-all',
                      textDecoration: showPreview ? 'line-through' : 'none',
                      opacity: showPreview ? 0.6 : 1,
                    }}
                  >
                    {currentValue !== null && currentValue !== '' ? currentValue : '(empty)'}
                  </div>

                  {/* New Value Preview (Only if selected and different) */}
                  {showPreview && (
                    <div
                      style={{
                        padding: '4px 8px',
                        background: '#f6ffed',
                        border: '1px dashed #b7eb8f',
                        borderRadius: 4,
                        fontSize: 13,
                        color: '#389e0d',
                        wordBreak: 'break-all',
                      }}
                    >
                      <Space size={4}>
                        <ArrowRightOutlined style={{ fontSize: 10 }} />
                        {newValue}
                      </Space>
                    </div>
                  )}

                  {/* Warning if season title missing */}
                  {!seasonTitleForLang && (
                    <div style={{ color: '#ccc', fontSize: 12 }}>(No season title)</div>
                  )}
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: (
        <Checkbox
          checked={previews.length > 0 && selectedIds.size === previews.length}
          indeterminate={selectedIds.size > 0 && selectedIds.size < previews.length}
          onChange={e => handleSelectAll(e.target.checked)}
        />
      ),
      key: 'checkbox',
      width: 50,
      render: (_: any, record: EpisodeTitlePreview) => (
        <Checkbox
          checked={selectedIds.has(record.id)}
          onChange={e => handleSelect(record.id, e.target.checked)}
        />
      ),
    },
  ];

  return (
    <Modal
      title="Batch Update Episode Titles"
      open={visible}
      onCancel={onClose}
      width={1100}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={updating}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          loading={updating}
          disabled={loading || selectedIds.size === 0}
        >
          Confirm ({selectedIds.size} update(s))
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 24, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
        <Text strong style={{ display: 'block', marginBottom: 12 }}>
          Title Format Settings ( {`{season}`}: Season Title, {`{number}`}: Episode Number )
        </Text>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="English" style={{ marginBottom: 0 }}>
              <Input
                value={formatPatterns.en}
                onChange={e => setFormatPatterns(prev => ({ ...prev, en: e.target.value }))}
                placeholder="{season} : Episode {number}"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Korean" style={{ marginBottom: 0 }}>
              <Input
                value={formatPatterns.ko}
                onChange={e => setFormatPatterns(prev => ({ ...prev, ko: e.target.value }))}
                placeholder="{season} : {number}회"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Chinese" style={{ marginBottom: 0 }}>
              <Input
                value={formatPatterns.zh}
                onChange={e => setFormatPatterns(prev => ({ ...prev, zh: e.target.value }))}
                placeholder="{season} : 第{number}集"
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>Loading content list...</p>
        </div>
      ) : previews.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
          <p>No contents linked to this season.</p>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
            <span>Total {previews.length} content(s)</span>
            <span>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
              <span style={{ color: '#52c41a' }}>
                Done: {previews.length - episodesWithChanges.length}
              </span>
            </span>
            <span>
              <EditOutlined style={{ color: '#faad14', marginRight: 4 }} />
              <span style={{ color: '#faad14' }}>Needs Update: {episodesWithChanges.length}</span>
            </span>
            <span style={{ marginLeft: 'auto', color: '#1890ff' }}>
              {selectedIds.size} selected
            </span>
          </div>
          <Table
            dataSource={previews}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ y: 400 }}
          />
        </>
      )}
    </Modal>
  );
};

export default EpisodeTitleBatchUpdateModal;
